import { useContext, useEffect, useState } from 'react'

import { Circle, EllipsisVertical } from 'lucide-react'
import type { FC } from 'react'
import { useLocation } from 'react-router-dom'

import type { ViewDTO } from '@/api/repository/repositorySchemas'
import { Button, buttonVariants } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { AuthContext } from '@/contexts/auth'
import { useSelectedApp } from '@/contexts/selectedApp'
import { useViews } from '@/contexts/views/useViews'
import { cn } from '@/lib/utils'
import { canCreate, canEdit, canRemove } from '@/pages/Admin/helpers'

import type { CustomFilterShape } from '../CustomFilter'
import { useQueryState } from '../QueryState/useQueryState'
import type { OperatorAndValue } from '../TableFilters'
import {
	removeEmptyOperatorAndValueOverrides,
	removeEmptyOverrides,
	removeTrueValues,
} from './helpers'
import ViewsForm from './ViewsForm'

export interface ViewManagerProps {
	filters: CustomFilterShape
	setViewFilters: (newFilters: CustomFilterShape) => void
	groupBy?: OperatorAndValue
	setViewGroupBy?: (newGroupBy: OperatorAndValue) => void
	columnVisibility?: Record<string, boolean>
	setViewColumnVisibility?: (
		newColumnVisibility: Record<string, boolean>
	) => void
	sort?: string
	setViewSort?: (newSort: string) => void
	columnOrder?: string[]
	setViewColumnOrder?: (newColumnOrder: string[]) => void
	resetUrlParams: () => void
}

export const DEFAULT_GLOBAL_VIEWS_NAME = 'Default View'
const NO_COLUMN_ORDER: string[] = []

const ViewsManager: FC<ViewManagerProps> = ({
	filters,
	setViewFilters,
	groupBy,
	setViewGroupBy,
	sort,
	setViewSort,
	columnVisibility,
	setViewColumnVisibility,
	columnOrder = NO_COLUMN_ORDER,
	setViewColumnOrder,
	resetUrlParams,
}) => {
	const { user: loggedInUser } = useContext(AuthContext)
	const [isRedirected, setIsRedirected] = useQueryState('redirected')

	const location = useLocation()
	const urlKey = location.pathname.split('/').at(-1)

	const { selectedApp: variant } = useSelectedApp()
	const { _id: variantId } = variant ?? { _id: '' }
	const appId = variant?.parent._id

	const hasOverrides =
		sort ||
		columnOrder?.length > 0 ||
		Object.entries({
			...filters,
			...groupBy,
			...columnVisibility,
		}).length > 0

	const {
		data: views,
		createView: createViewRequest,
		deleteView,
		updateView: editView,
	} = useViews(appId, urlKey)

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDuplicating, setIsDuplicating] = useState(false)
	const [viewId, setViewId] = useQueryState<number>('viewId')

	const LSKey = `${variantId}:${urlKey}`
	const LSSelectedViewKey = `${LSKey}:selectedView`

	const lastSelectedViewId = localStorage.getItem(LSSelectedViewKey)

	const lastSelectedView =
		lastSelectedViewId && views?.length > 0
			? views.find(view => view._id === lastSelectedViewId) ?? views[0]
			: views?.length > 0
				? views[0]
				: null

	const [selectedView, setSelectedView] = useState<ViewDTO>(null)

	useEffect(() => {
		if (views && views.length > 0 && !selectedView && !viewId) {
			setView(lastSelectedView, !!isRedirected, false)
		}
	}, [views, selectedView, lastSelectedView])

	useEffect(() => {
		if (selectedView && !viewId) {
			setViewId(selectedView.viewNumber)
		}
	}, [selectedView, viewId])

	useEffect(() => {
		if (
			views &&
			views.length > 0 &&
			(!selectedView || selectedView.viewNumber !== viewId)
		) {
			const view = views.find(v => v.viewNumber === viewId * 1)
			if (!view) {
				setView(lastSelectedView ?? views[0], !!isRedirected, false)
				return
			}
			setView(view, !!isRedirected, false)
		}
	}, [viewId, views])

	const setView = (view: ViewDTO, ignoreConfig = false, reset = true) => {
		if (!view) return
		setSelectedView(view)
		if (!ignoreConfig) {
			setViewFilters(view.config?.filters ?? {})
			if (setViewGroupBy) {
				setViewGroupBy(view.config?.groupBy as OperatorAndValue)
			}
			if (setViewSort) {
				setViewSort(view.config?.sort)
			}
			if (reset) {
				resetUrlParams()
			}
		}
		if (setViewColumnVisibility) {
			setViewColumnVisibility(view.config?.columnVisibility ?? {})
		}
		if (setViewColumnOrder) {
			setViewColumnOrder(view.config?.columnOrder ?? [])
		}
		localStorage.setItem(LSSelectedViewKey, view._id)
		setViewId(view.viewNumber ?? 0)
		setIsRedirected(undefined)
	}

	const createView = ({
		name,
		config = {},
	}: {
		name: string
		config?: ViewDTO['config']
	}) => {
		createViewRequest.mutate(
			{
				body: {
					name,
					url: urlKey,
					parentId: appId,
					config: config ?? {},
				},
				pathParams: {
					parentId: appId,
				},
			},
			{
				onSuccess: (justCreatedView: ViewDTO) => {
					setView(justCreatedView)
				},
			}
		)
	}

	const updateView = (view: ViewDTO) => {
		if (view.url !== urlKey) return

		editView.mutate(
			{
				pathParams: {
					parentId: view.parentId,
					viewId: view._id,
				},
				body: view,
			},
			{
				onSuccess: (justEditedView: ViewDTO) => {
					setView(justEditedView)
				},
			}
		)
	}

	const removeView = (view: ViewDTO) => {
		if (view.viewNumber === 0) {
			return
		}

		deleteView.mutate(
			{
				pathParams: {
					parentId: view.parentId,
					viewId: view._id,
				},
			},
			{
				onSuccess: () => {
					if (view._id === selectedView?._id)
						onViewSelect(views[0]._id)
				},
			}
		)
	}

	const onViewSave = (name: string) => {
		const existingView = views.find(
			view => view.name.toLowerCase().trim() === name.toLowerCase().trim()
		)

		const filtersWithoutOverrides = removeEmptyOverrides({
			...selectedView?.config?.filters,
			...filters,
		})
		const groupByWithoutOverrides = removeEmptyOperatorAndValueOverrides({
			...selectedView?.config?.groupBy,
			...groupBy,
		} as OperatorAndValue)

		const hiddenColumns = removeTrueValues({
			...selectedView?.config?.columnVisibility,
			...columnVisibility,
		})

		const newColumnOrder =
			(columnOrder?.length > 0
				? columnOrder
				: selectedView?.config?.columnOrder) ?? []

		const config = {
			...selectedView?.config,
			filters: filtersWithoutOverrides,
			groupBy: groupByWithoutOverrides,
			...(sort && { sort }),
			columnVisibility: hiddenColumns,
			columnOrder: newColumnOrder,
		}

		if (existingView?._id) {
			updateView({
				...existingView,
				//@ts-ignore todo: fix
				config,
			})
		} else {
			createView({
				name,
				//@ts-ignore todo: fix
				config: config,
			})
		}
	}

	const onViewSelect = (viewId: string) => {
		const view = views.find(currentView => currentView._id === viewId)
		if (!view) return
		setView(view)
	}

	const handleClose = () => {
		setIsModalOpen(false)
		setIsDuplicating(false)
	}

	const onSave = ({ name }: { name: string }) => {
		onViewSave(name)
		setIsModalOpen(false)
	}

	if (!views) return null

	const showSaveOption = canCreate(variant?.role) || canEdit(variant?.role)
	const showDeleteOption =
		canRemove(variant?.role) &&
		selectedView &&
		selectedView.viewNumber !== 0

	return (
		<>
			<Dialog
				open={isModalOpen || isDuplicating}
				onOpenChange={open => {
					if (!open) handleClose()
				}}
			>
				<DialogContent className="text-foreground">
					<ViewsForm
						onSave={onSave}
						onClose={handleClose}
						name={
							selectedView?.name === DEFAULT_GLOBAL_VIEWS_NAME
								? loggedInUser?.isAdmin
									? selectedView?.name
									: ''
								: selectedView?.name
						}
						isAdmin={loggedInUser?.isAdmin}
					/>
				</DialogContent>
			</Dialog>

			<div className="flex flex-row items-center justify-end w-full text-foreground">
				{hasOverrides && (
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger>
								<Circle className="w-3 h-3 fill-current text-primary mr-2" />
							</TooltipTrigger>
							<TooltipContent>
								<p>The view has unsaved changes</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)}

				<div className="flex items-center max-h-[30px] max-w-[200px] ml-2">
					{views.length > 0 && (
						<Select
							value={selectedView?._id}
							onValueChange={onViewSelect}
						>
							<SelectTrigger
								className="h-[30px]"
								id="select-view"
								aria-label="Select view"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{views.map(({ name, _id }) => (
									<SelectItem key={_id} value={_id}>
										{name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}

					{(showSaveOption || showDeleteOption) && (
						<DropdownMenu>
							<DropdownMenuTrigger
								className={cn(
									buttonVariants({
										variant: 'ghost',
										size: 'sm',
									}),
									'ml-1 h-[30px] px-2'
								)}
								aria-label="View options"
							>
								<EllipsisVertical />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{showSaveOption && (
									<DropdownMenuItem
										onClick={() => setIsModalOpen(true)}
									>
										Save as...
									</DropdownMenuItem>
								)}
								{showDeleteOption && (
									<DropdownMenuItem
										onClick={() => removeView(selectedView)}
									>
										Delete
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>
		</>
	)
}

export default ViewsManager
